import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Props for {@link UpdateAvailableDialog}.
 */
export interface UpdateAvailableDialogProps {
  /** Controls dialog visibility. */
  open: boolean;
  /** Called when the dialog requests an open-state change. */
  onOpenChange: (open: boolean) => void;
  /** The newly detected version string (informational, currently unused in UI). */
  newVersion: string | null;
}

/**
 * Non-dismissible modal that notifies users a new deployment is available
 * and offers a single action: refresh the page.
 *
 * @param open - Whether the dialog is currently visible.
 * @param onOpenChange - Callback to update the open state (close is prevented).
 * @param newVersion - The detected new version string.
 */
export const UpdateAvailableDialog = ({
  open,
  onOpenChange,
  newVersion: _newVersion,
}: UpdateAvailableDialogProps): JSX.Element => {
  const handleRefresh = (): void => {
    // Persist the new version so the next mount doesn't re-trigger the dialog
    if (_newVersion) {
      try {
        localStorage.setItem("app-version", _newVersion);
      } catch {
        // localStorage may be unavailable — non-critical
      }
    }
    window.location.reload();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        // Prevent closing — only refresh action is available
        if (!value) {
          return;
        }
        onOpenChange(value);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>¡Hay una nueva versión disponible!</DialogTitle>
          <DialogDescription>
            Una nueva versión está disponible. Actualiza para aplicar los cambios.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={handleRefresh}>Actualizar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
